<template>
  <component
    :is="isModalMode ? WuiModal : 'section'"
    v-click-outside="onOutsideClick"
    class="flex flex-col grow"
    :class="{
      'h-[85vh] phone:h-[95vh] !w-[374px] min-w-[360px] max-w-[374px] short:h-[90vh]': isModalMode,
      'overflow-auto will-change-scroll bg-bg-base-bg3': !isModalMode,
    }"
    v-bind="isModalMode ? { title: $t('mainAside.newChat'), size: 40 } : {}"
    @close="onOutsideClick"
  >
    <header
      v-if="!isModalMode"
      class="flex items-center justify-between p-2 bg-elev-header-bg"
    >
      <h2 class="text-heading-sb-18-24 text-fg-base-fg3 p-2">
        {{ $t('mainAside.newChat') }}
      </h2>
      <app-link
        v-model="navState"
        :to="NavState.CHATS"
        class="cursor-pointer hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres rounded-8 p-2"
      >
        <wui-icon
          name="closeRegularMdM"
          :title="$t('common.close')"
          class="text-fg-base-fg10"
        />
      </app-link>
    </header>
    <main class="flex min-h-0 flex-col grow overflow-auto will-change-scroll">
      <div class="mb-[6px] bg-bg-base-bg1 p-4 rounded-b-12">
        <div class="flex items-center gap-x-3 mb-3">
          <chat-avatar-input
            :avatar-image="avatarImage!"
            @set-avatar="setAvatar($event)"
            @clear-avatar="clearAvatar"
          />
          <wui-input
            v-model.trim="nameChat"
            data-test="chat-name-input"
            :placeholder="$t('common.groupName')"
            maxlength="255"
            class="w-full"
          />
        </div>
        <chat-mode-select
          v-model="selectedRoomType"
          class="w-full!"
        />
        <div
          v-show="isModerated"
          class="mt-4"
        >
          <wui-input-switch
            v-model="needToEnterPinCode"
            :label="needToEnterPinCodePlaceholder"
            small
          />
        </div>
      </div>
      <chat-subscribers-picker
        v-model="selectedSubscribers"
        :is-moderated="isModerated"
        class="min-h-0 overflow-auto will-change-scroll"
        @toggle-role="toggleRole"
      />
    </main>
    <template
      v-if="isModalMode"
      #footer
    >
      <wui-btn
        :disabled="selectedSubscribers.length < 1"
        auto-loading
        :size="40"
        variant="brand"
        state="filled"
        @click="createChat"
      >
        {{ $t('common.create') }}
      </wui-btn>
    </template>
    <footer
      v-if="!isModalMode"
      class="px-4 py-5 bg-bg-base-bg1 w-full"
    >
      <wui-btn
        :disabled="selectedSubscribers.length < 1"
        auto-loading
        :size="40"
        class="w-full!"
        variant="brand"
        state="filled"
        @click="createChat"
      >
        {{ $t('common.create') }}
      </wui-btn>
    </footer>
  </component>
</template>

<script lang="ts" setup>
import {
  WuiBtn,
  WuiInputSwitch,
  WuiModal,
  useDialog,
  closeDialogKey,
  ClickOutside as vClickOutside,
  WuiInput,
  WuiIcon,
  safeInject, useRenderNotifier,
} from '@wui/common-library'
import { ConferenceMemberRole, ConferenceType, useIM } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'


import { useCreateRoomOptions , ChatModeSelect, ChatSubscribersPicker } from '@/features/groups-chat'


import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'
import { useRoom } from '@/entities/use-room'

import { ChatAvatarInput } from '@/shared/components/avatar-input'
import { AppLink } from '@/shared/components/buttons'
import { SuccessDialog } from '@/shared/components/dialogs'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useSidebar } from '@/shared/composables/use-sidebar'
import { Contact } from '@/shared/types/api'
import { NavState } from '@/shared/types/app'
import { PSTNGUEST } from '@/shared/utils/constants'
import { createFile } from '@/shared/utils/convert-uploading-files'
import { getTextForDifferentCount } from '@/shared/utils/get-text-for-different-count'
import { optionalPush } from '@/shared/utils/router'
import { fromPServedUser } from '@/shared/utils/uri'
import { getChatRoute } from '@/shared/utils/url'

type CreateChatResult = {
  roomUri: string,
  pinCode?: string
}

const props = defineProps<{
    isModalMode?: boolean
    contact?: Contact
    mode?: ConferenceType
  }>()

const { currentUser } = useCurrentUser()
const { navState } = useSidebar()
if (!currentUser.value) throw new Error()
const { awaitForRoomAdded } = useStoreRooms()
const { changeAvatar } = useRoom()
const { t, tc } = useLocalization()
const { showDialog } = useDialog()
const { notifyError } = useRenderNotifier()

const nameChat = ref<string>('')
const avatarImage = ref<File | string>()

const addContactToList = function (contact: Contact) {
  if ('isSelected' in contact) return
  selectedSubscribers.value.push({
    role: contact.imLogin ? 'User' : 'External',
    number: contact.mobilePhone && !contact.imLogin ? contact.mobilePhone : contact.internalNumber,
  })
}

const { allContacts } = storeToRefs(useStoreContacts())
const selectedContacts = computed<Contact[]>(() => {
  const numbers = selectedSubscribers.value.map(s => {
    if (s.role === 'External' && s.number.startsWith(PSTNGUEST)) {
      return s.number.split('-').at(1)
    }

    return s.number
  })

  return allContacts.value.filter(c => numbers.includes(c.internalNumber) || numbers.includes(c.mobilePhone ?? undefined))
})

const {
  selectedRoomType,
  isModerated,
  selectedSubscribers,
  needToEnterPinCode,
  needToEnterPinCodePlaceholder,
} = useCreateRoomOptions(selectedContacts)

const setAvatar = (image: File | string | undefined) => {
  if (!image) return
  avatarImage.value = image
}
const clearAvatar = () => {
  avatarImage.value = undefined
}
const validateFields = () => {
  if (selectedSubscribers.value.length) {
    return true
  } else {
    notifyError(t('notifications.chatNotCreatedNoSubscribers'))
    return false
  }
}

const writeNames = () => {
  if (nameChat.value) {
    return
  }

  if (currentUser.value?.name) {
    if (currentUser.value?.lastName) {
      nameChat.value = `${currentUser.value?.lastName} ${currentUser.value?.firstName}`
    } else {
      nameChat.value = currentUser.value?.name
    }
  }

  const WRITABLE_CONTACTS_NAMES_COUNT = 2
  if (selectedContacts.value.length <= WRITABLE_CONTACTS_NAMES_COUNT) {
    const mapFn = (c: Contact) => {
      if (c.lastName) {
        return `${c.lastName} ${c.firstName}`
      }
      return c.name
    }

    nameChat.value += `, ${selectedContacts.value.map(mapFn).join(', ')}`
  } else {
    const count = selectedContacts.value.length - WRITABLE_CONTACTS_NAMES_COUNT
    const wordsForCountMemberText = {
      forOne: tc('common.participant', 0),
      forTwo: tc('common.participant', 1),
      forFive: tc('common.participant', 2),
    }

    nameChat.value +=
        `, ${(selectedContacts.value.at(0) as Contact).name}, 
        ${(selectedContacts.value.at(1) as Contact).name} 
        ${t('mainAside.andMore')} 
        ${getTextForDifferentCount(count, wordsForCountMemberText)}
        `
  }
}

const createChat = async () => {
  if (!validateFields()) return
  writeNames()

  try {
    const result = await saveRoom()

    if (!result || !result.roomUri) return
    const roomUri = result.roomUri
    const roomId = fromPServedUser(roomUri)
    await awaitForRoomAdded(roomId)

    if (avatarImage.value) {
      const executor = currentUser.value?.imLogin ?? ''
      let avatarUploaded = false

      if (typeof avatarImage.value === 'string') {
        const fileView = await createFile(avatarImage.value)
        avatarUploaded = await changeAvatar(fileView, roomId, roomUri, executor, avatarImage.value)
      } else {
        avatarUploaded = await changeAvatar(avatarImage.value, roomId, roomUri, executor, '')
      }

      if (!avatarUploaded) {
        notifyError(t('mainAside.failedToLoadAvatar'))
      }
    }
    let information = `${t('mainAside.chatHasBeenCreated')}`

    if (result.pinCode) {
      information += `. ${t('common.pinCode')}: ${result.pinCode}`
    }

    await showDialog(SuccessDialog, { information, timeout: 10000, hasOverlay: true })
    await optionalPush(getChatRoute(roomId))

    selectedRoomType.value = ConferenceType.FREE_FOR_ALL
    avatarImage.value = undefined

    selectedSubscribers.value = []
    nameChat.value = ''

    close?.()
  } catch (e: unknown) {
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine
    const errorMessage = e instanceof Error ? e.message : ''
    const isNetworkError = /failed to fetch|networkerror|network error|net::err_/i.test(errorMessage)
    notifyError(
      isOffline || isNetworkError
        ? t('common.connectionLost')
        : errorMessage || t('common.unknownError'),
    )
  }
}

const { createConference, enableConferencePinCode } = useIM()
const saveRoom = async (): Promise<CreateChatResult | undefined> => {
  if (!currentUser.value) return
  return createConference({
    mode: selectedRoomType.value,
    name: nameChat.value,
    subscribers: selectedSubscribers.value,
  }).then((response) => {
    return {
      roomUri: response.item.pServed,
    }
  })
    .then((createChatResponse: CreateChatResult) => {
      if (needToEnterPinCode.value) {
        return enableConferencePinCode({
          pServed: createChatResponse.roomUri,
        }).then(({ item: { pinCode = '' }}) => {
          return {
            roomUri: createChatResponse.roomUri,
            pinCode,
          }
        })
      }

      return {
        roomUri: createChatResponse.roomUri,
      }
    })
}

let close: () => void
if (props.isModalMode) {
  close = safeInject<() => void>(closeDialogKey)
} else {
  close = () => {
    navState.value = NavState.CHATS
  }
}

const toggleRole = (internalNumber: string, role: ConferenceMemberRole) => {
  selectedSubscribers.value.forEach((s) => {
    if (s.number === internalNumber) {
      if (role === 'Moderator') {
        s.role = 'User'
      } else {
        s.role = 'Moderator'
      }
    }
  })
}

const onOutsideClick = () => {
  if (props.isModalMode) {
    close()
  }
}


if (props.contact) {
  addContactToList(props.contact)
}

if (props.mode) {
  selectedRoomType.value = props.mode
}
</script>
